/**
 * RenewalPipeline — the Renewal-Pipeline bucket bars (Story UI-U7, AC 5c).
 *
 * Reuses the EXISTING GET /renewals/heute wire via useHeuteRenewals (zero new renewals
 * code). Bars render per bucket in RENEWAL_BUCKETS order with the REAL bucket labels
 * (Überfällig / ≤ 14 Tage / ≤ 30 Tage / ≤ 3 Monate / ≤ 6 Monate / > 6 Monate) — the
 * sketched 60/90 bands do NOT exist in the api and are NOT faked. `unknown` renders only
 * when > 0. Semantic override (design.md §2): overdue = Danger, le_14d = Warning, others
 * neutral chart ramp. Plain divs (no chart lib). On query error the card shows the standard
 * error state; the page still renders.
 *
 * [Source: apps/web/src/features/dashboard/hooks/useHeuteRenewals.ts]
 * [Source: apps/web/src/features/renewals/mock.ts#BUCKET_META]
 * [Source: packages/shared/src/renewals.ts#RENEWAL_BUCKETS]
 * [Source: docs/stories/ui-u7-portfolio.md AC 5c + Dev Notes §5]
 */

import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import type { RenewalBucket } from '@brokly/shared';
import { RENEWAL_BUCKETS } from '@brokly/shared';
import { Card } from '../../../components/ui/Card';
import { useHeuteRenewals } from '../../dashboard/hooks/useHeuteRenewals';
import { BUCKET_META } from '../../renewals/mock';

/** design.md §2 time-urgency semantics OVERRIDE the chart ramp. */
function barColor(bucket: RenewalBucket): string {
  if (bucket === 'overdue') return 'var(--sem-alert)';
  if (bucket === 'le_14d') return 'var(--sem-decide)';
  return 'var(--chart-1)';
}

export function RenewalPipeline() {
  const { data, isError } = useHeuteRenewals();

  return (
    <Card className="rounded-xl p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm font-medium">Renewal-Pipeline</div>
        <Link
          to="/renewals"
          className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          Alle anzeigen <ArrowRight className="size-3" />
        </Link>
      </div>

      {isError || !data ? (
        <div className="grid h-32 place-items-center text-sm text-muted-foreground">
          Fälligkeiten konnten nicht geladen werden.
        </div>
      ) : (
        <ul className="space-y-2">
          {RENEWAL_BUCKETS.filter(
            (b) => b !== 'unknown' || (data.bucketCounts[b] ?? 0) > 0,
          ).map((bucket) => {
            const count = data.bucketCounts[bucket] ?? 0;
            const max = Math.max(1, ...RENEWAL_BUCKETS.map((b) => data.bucketCounts[b] ?? 0));
            const widthPct = Math.round((count / max) * 100);
            return (
              <li key={bucket}>
                <Link to="/renewals" className="group block">
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground group-hover:text-foreground">
                      {BUCKET_META[bucket].label}
                    </span>
                    <span className="mono-num font-medium text-foreground">{count}</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${widthPct}%`, background: barColor(bucket) }}
                    />
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
